import { useEffect, useState, useRef } from 'react';
import useKeyPress from '@Components/Utilities/KeyPress';
import translator from '@Components/Utilities/Translator';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { setIOSScrollLock, removeIOSScrollLock } from '@Components/Utilities/SandhillsUtility';
import { useRecoilState } from 'recoil';
import atoms from '@Components/Utilities/SandhillsAtoms';

const Modal = (props) => {
    const modalRef = useRef(null);
    const [isInitialRender, setIsInitialRender] = useState(true);
    const windowSize = useWindowSize();
    const [IsModalOpen, setIsModalOpen] = useRecoilState(atoms.isModalOpen);

    if (props.onEscapeKeyClose) {
        useKeyPress('Escape', props.onEscapeKeyClose);
    }

    if (props.onEnterKeySubmit) {
        useKeyPress('Enter', props.onEnterKeySubmit);
    }

    useEffect(function setBodyOverFlow() {
        if (!props.allowScroll) {
            if (!props.setDisplayNone) {
                setIOSScrollLock(windowSize.isMobile);
            }

            return () => removeIOSScrollLock(windowSize.isMobile);
        }
    }, []);

    useEffect(() => {
        if (!(props.setDisplayNone || props.hidden)) {
            setIsModalOpen(true);
        } else if (!isInitialRender) {
            setIsModalOpen(false);
        }

        return () => {
            setIsModalOpen(false)
        };
    }, [props.setDisplayNone, props.hidden]);

    useEffect(function setEventHandlers() {
        const modalContainer = modalRef.current;

        if (isInitialRender) {
            const focusableNodes = getFocusableNodeList(modalContainer);

            if (focusableNodes && focusableNodes.length > 0) {
                let nodelistArray = [].slice.call(focusableNodes);
                let firstValidInput = null;

                if (props.isGlobalBrandsModal) {
                    const firstLink = nodelistArray.find((node) => node.nodeName.toUpperCase() === 'A');
                    const firstDiv = nodelistArray.find((node) => node.nodeName.toUpperCase() === 'DIV');

                    firstValidInput = firstDiv || firstLink;
                }
                else {
                    const firstInput = nodelistArray.find((node) => node.nodeName.toUpperCase() === 'INPUT');
                    const firstBtn = nodelistArray.find((node) => node.nodeName.toUpperCase() === 'BUTTON');

                    firstValidInput = firstInput || firstBtn;
                }

                if (firstValidInput) {
                    firstValidInput.focus();
                    setIsInitialRender(!isInitialRender);
                }
            }
        }

        modalContainer.addEventListener('keydown', handleFocus);

        return () => {
            modalContainer.removeEventListener('keydown', handleFocus);
        }
    });

    const getFocusableNodeList = (modalContainer) => {
        let focusableNodes = null;
        let displayableNodes = null

        if (modalContainer) {
            focusableNodes = modalContainer.querySelectorAll('a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
        }

        displayableNodes = getDisplayableNodes(focusableNodes);

        return displayableNodes;
    };

    const getDisplayableNodes = (focusableNodes) => {
        var filtered = [].filter.call(focusableNodes, function (node) {
            var style = window.getComputedStyle(node);
            return (style.display !== 'none');
        });

        return filtered;
    };

    const handleFocus = (e) => {
        const modalContainer = modalRef.current;
        const focusableElements = getFocusableNodeList(modalContainer);

        if (props.alterFocus) {
            return;
        }

        if (focusableElements && focusableElements.length > 0) {
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            let isTabKeyPressed = e.key === 'Tab' || e.keyCode === 9;

            if (!isTabKeyPressed) {
                return;
            }

            if (e.shiftKey) {
                if (firstElement === document.activeElement) {
                    lastElement.focus();
                    e.preventDefault();
                }
            } else {
                if (lastElement === document.activeElement) {
                    firstElement.focus();
                    e.preventDefault();
                }
            }
        }
    };

    return (
        <div className={'ts-modal-container ' + (props.containerClassName ? props.containerClassName : '')} style={{ display: props.setDisplayNone && 'none', visibility: props.hidden && 'hidden' }} role='dialog' aria-modal='true' aria-labelledby='dialog-label' aria-describedby='description-label' ref={modalRef}>
            <div className={'ts-modal-content ' + (props.contentClassName ? props.contentClassName : '')}>
                {props.children}
            </div>
        </div>
    );
};

const ModalHeader = (props) => {
    return (
        <div className={'ts-modal-header des-modal-header ' + (props.headerClassName ? props.headerClassName : '')}>
            {(props.onClose || props.displayCloseIconOnly) &&
                <button type='button' className='close ts-button' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.close')} onClick={props.onClose}>
                    <i className={props.xMarkClassName ? props.xMarkClassName : 'fas fa-times'}></i>
                </button>}
            {props.children}
        </div>
    );
};

const ModalTitle = (props) => {
    return (
        <>
            {props.children && (
                <h2 className={'ts-modal-title des-modal-title ' + (props.headingClassName ? props.headingClassName : '')} id="dialog-label">
                    {props.children}
                </h2>
            )}
        </>
    );
};

const ModalBody = (props) => {
    return (
        <div className={'ts-modal-body des-modal-body ' + (props.bodyClassName ? props.bodyClassName : '')}>
            {props.children}
        </div>
    );
};

const ModalFooter = (props) => {
    return (
        <div className='ts-modal-footer des-modal-footer'>
            {props.children}
        </div>
    );
};

export default Object.assign(Modal, {
    Body: ModalBody,
    Header: ModalHeader,
    Footer: ModalFooter,
    Title: ModalTitle,
});